import type { Partner } from "@expert/shared-types";

import { getAppConfigSingleton } from "@expert/app-config";
import { createContext, useContext, useState } from "react";

import { type ExpertUniversityConfigurationContextType, EnabledPartners as enabledPartners } from "../types";

export const ExpertUniversityConfigurationContext = createContext<ExpertUniversityConfigurationContextType | undefined>(
    undefined,
);

interface ExpertUniversityProviderProps {
    children?: React.ReactNode;
    userCache: Record<string, any>;
    partner: Partner;
}

export function ExpertUniversityConfigurationProvider({
    children,
    userCache,
    partner,
}: React.PropsWithChildren<ExpertUniversityProviderProps>) {
    const PartnerToProfileMap: Partial<Record<Partner, string>> = {
        verizon: "paula_smith",
        att: "andrew_mayweather",
        amazonrouter: "amazon_sandbox",
    };
    const PROFILE_TESTING_GROUP = "HQDOMAIN_APP-NON-VZW-APP-SOLUTO-TRAINING-ASSIST-";
    const { allowedPartners: appConfigAllowedPartners } = getAppConfigSingleton();
    //Display all enabled partners if user is in testing group
    const shouldDisplayAllEnabledPartners = userCache.groups.includes(PROFILE_TESTING_GROUP);
    const [taskInSandbox, setTaskInSandbox] = useState<boolean>(false);
    const [trainingPartner, setTrainingPartner] = useState<Partner>(partner);
    const [mostRecentSessionId, setMostRecentSessionId] = useState<string | undefined>(undefined);
    const sandboxProfileId = PartnerToProfileMap[trainingPartner] ?? "";

    return (
        <ExpertUniversityConfigurationContext.Provider
            value={{
                allowedPartners: shouldDisplayAllEnabledPartners ? [...enabledPartners] : appConfigAllowedPartners,
                taskInSandbox,
                mostRecentSessionId,
                sandboxProfileId,
                setMostRecentSessionId,
                setTaskInSandbox,
                setTrainingPartner: (partner: React.SetStateAction<Partner>) => {
                    setMostRecentSessionId(undefined);
                    setTrainingPartner(partner);
                },
                trainingPartner,
            }}
        >
            {children}
        </ExpertUniversityConfigurationContext.Provider>
    );
}

export function useExpertUniversityConfiguration() {
    const context = useContext(ExpertUniversityConfigurationContext);
    if (!context) {
        throw new Error(
            "useExpertUniversityConfiguration must be used within an ExpertUniversityConfigurationProvider",
        );
    }
    return context;
}
